import type {
  ShellHeaderActions,
  ShellHeaderBreadcrumb,
  ShellHeaderPatch,
} from '@wandb/forge-shell-contract';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type ShellHeaderContextValue = {
  /** Breadcrumbs published by the current lease, or null if it published none. */
  breadcrumbs: readonly ShellHeaderBreadcrumb[] | null;
  showProductCrumb: boolean;
  /** Header controls published by the current lease; empty if none. */
  actions: ShellHeaderActions;
  /** Claims header ownership and returns operations scoped to that owner. */
  claimHeader: () => ShellHeaderLease;
};

type ShellHeaderLease = {
  patchHeader: (patch: ShellHeaderPatch) => void;
  release: () => void;
};

export const NO_SHELL_HEADER_ACTIONS: ShellHeaderActions = {};

const ShellHeaderContext = createContext<ShellHeaderContextValue | null>(null);

export const ShellHeaderProvider = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const [breadcrumbs, setBreadcrumbs] = useState<
    readonly ShellHeaderBreadcrumb[] | null
  >(null);
  const [showProductCrumb, setShowProductCrumb] = useState(true);
  const [actions, setActions] = useState<ShellHeaderActions>(
    NO_SHELL_HEADER_ACTIONS
  );
  const ownerRef = useRef<symbol | null>(null);

  const claimHeader = useCallback(() => {
    // Each lease gets a unique owner token. A stale remote can neither update
    // nor clear the header after a newer remote replaces this token.
    const owner = Symbol('shell-header-owner');
    ownerRef.current = owner;

    return {
      patchHeader: (patch: ShellHeaderPatch) => {
        if (ownerRef.current !== owner) {
          return;
        }
        if (patch.breadcrumbs != null) {
          setBreadcrumbs(patch.breadcrumbs);
        }
        if (patch.showProductCrumb != null) {
          setShowProductCrumb(patch.showProductCrumb);
        }
        if (patch.actions != null) {
          setActions(patch.actions);
        }
      },
      release: () => {
        if (ownerRef.current !== owner) {
          return;
        }
        ownerRef.current = null;
        setBreadcrumbs(null);
        setShowProductCrumb(true);
        setActions(NO_SHELL_HEADER_ACTIONS);
      },
    };
  }, []);

  const value = useMemo(
    () => ({
      breadcrumbs,
      showProductCrumb,
      actions,
      claimHeader,
    }),
    [breadcrumbs, showProductCrumb, actions, claimHeader]
  );

  return (
    <ShellHeaderContext.Provider value={value}>
      {children}
    </ShellHeaderContext.Provider>
  );
};

export const useShellHeaderBreadcrumbs = ():
  | readonly ShellHeaderBreadcrumb[]
  | null => {
  const context = useContext(ShellHeaderContext);
  return context?.breadcrumbs ?? null;
};

export const useShellHeaderShowProductCrumb = (): boolean => {
  const context = useContext(ShellHeaderContext);
  return context?.showProductCrumb ?? true;
};

export const useShellHeaderActions = (): ShellHeaderActions => {
  const context = useContext(ShellHeaderContext);
  return context?.actions ?? NO_SHELL_HEADER_ACTIONS;
};

export const useClaimShellHeader = () => {
  const context = useContext(ShellHeaderContext);
  return context?.claimHeader;
};

/**
 * Claims a lease on the shell header when a remote is mounted,
 * and releases it when the remote is unmounted. Returns patch
 * function that allows the remote to update the header contents.
 */
export const useRemotePatchHeader = (
  enabled: boolean
): ((patch: ShellHeaderPatch) => void) | undefined => {
  const claimHeader = useClaimShellHeader();
  const canClaim = enabled && claimHeader != null;
  const leaseRef = useRef<ShellHeaderLease | null>(null);

  useEffect(() => {
    if (!canClaim) {
      return;
    }

    const lease = claimHeader();
    leaseRef.current = lease;

    return () => {
      leaseRef.current = null;
      lease.release();
    };
  }, [canClaim, claimHeader]);

  const patchHeader = useCallback((patch: ShellHeaderPatch) => {
    leaseRef.current?.patchHeader(patch);
  }, []);

  return canClaim ? patchHeader : undefined;
};
